import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';

const Scammers = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scammers, setScammers] = useState([]);
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScammer, setSelectedScammer] = useState(null);

  const checkAccess = useCallback(async () => {
    try {
      const response = await axios.get('/api/scammers/check-access', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAccessStatus(response.data.data);
    } catch (error) {
      console.error('Failed to check access:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScammers = useCallback(async () => {
    try {
      const response = await axios.get(`/api/scammers?page=${page}&limit=12${searchTerm ? `&search=${searchTerm}` : ''}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setScammers(response.data.data.scammers);
      setTotalPages(response.data.data.totalPages);
    } catch (error) {
      console.error('Failed to fetch scammers:', error);
    }
  }, [page, searchTerm]);

  useEffect(() => {
    if (isAuthenticated) {
      checkAccess();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, checkAccess]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!accessStatus?.hasAccess) return;
    
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      fetchScammers();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [accessStatus, fetchScammers]);

  const handleRequestAccess = async () => {
    if (!requestReason.trim()) {
      alert('Please provide a reason for your request');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/scammers/request-access', 
        { requestReason },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      alert('Access request submitted successfully! You will be notified once reviewed.');
      checkAccess();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ 
          display: 'inline-block',
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255,255,255,0.1)',
          borderTop: '4px solid var(--accent)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>🔒 Login Required</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Please log in to access the scammers database
          </p>
          <Link to="/login">
            <button className="btn-primary">Log In</button>
          </Link>
        </div>
      </div>
    );
  }

  // Request Access UI
  if (!accessStatus?.hasAccess) {
    return (
      <div className="container" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🛡️</div>
          <h1 style={{ marginBottom: '15px' }}>Scammers Database</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '30px', fontSize: '1.05rem' }}>
            This page contains information about known scammers in the gaming community.
            Access is restricted to approved users to maintain data integrity.
          </p>

          {accessStatus?.status === 'pending' ? (
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: 'rgba(234, 179, 8, 0.1)',
              border: '1px solid rgba(234, 179, 8, 0.3)'
            }}>
              <h3 style={{ color: '#fbbf24', marginBottom: '10px' }}>⏳ Request Pending</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Your access request is currently under review. You'll be notified once it's processed.
              </p>
            </div>
          ) : accessStatus?.status === 'rejected' ? (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#dc2626', marginBottom: '10px' }}>❌ Request Rejected</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: accessStatus?.rejectionReason ? '15px' : '0' }}>
                  Your previous access request was rejected. You can submit a new request below.
                </p>
                {accessStatus?.rejectionReason && (
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    borderRadius: '8px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    border: '1px solid rgba(220, 38, 38, 0.3)'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#dc2626' }}>
                      Reason for rejection:
                    </p>
                    <p style={{ color: '#fca5a5', lineHeight: '1.6', margin: 0, fontSize: '0.95rem' }}>
                      {accessStatus.rejectionReason}
                    </p>
                  </div>
                )}
              </div>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Please explain why you need access to this database..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  marginBottom: '15px',
                  resize: 'vertical'
                }}
              />
              <button 
                className="btn-primary" 
                onClick={handleRequestAccess}
                disabled={submitting}
                style={{ width: '100%' }}
              >
                {submitting ? 'Submitting...' : 'Resubmit Access Request'}
              </button>
            </div>
          ) : (
            <div>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Please explain why you need access to this database..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  marginBottom: '15px',
                  resize: 'vertical'
                }}
              />
              <button 
                className="btn-primary" 
                onClick={handleRequestAccess}
                disabled={submitting}
                style={{ width: '100%' }}
              >
                {submitting ? 'Submitting...' : 'Request Access'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Scammers List UI (for approved users)
  return (
    <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
      {/* Header Section with Gradient */}
      <div style={{ 
        marginBottom: '40px',
        padding: '40px 30px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.15) 0%, rgba(153, 27, 27, 0.1) 100%)',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          fontSize: '200px',
          opacity: '0.05'
        }}>🛡️</div>
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h1 style={{ 
                fontSize: '2.8rem', 
                marginBottom: '12px',
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '800'
              }}>
                🛡️ Scammer Database
              </h1>
              <p style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '1.1rem',
                maxWidth: '600px',
                lineHeight: '1.6'
              }}>
                Community-driven database documenting known scammers to protect gamers worldwide
              </p>
              <div style={{ 
                marginTop: '15px',
                display: 'flex',
                gap: '20px',
                fontSize: '0.95rem',
                color: 'var(--text-secondary)'
              }}>
                <span>📊 {scammers.length} entries found</span>
                <span>👥 Community protected</span>
              </div>
            </div>
            <Link to="/scammers/add" style={{ textDecoration: 'none' }}>
              <button className="btn-primary" style={{ 
                padding: '14px 28px',
                fontSize: '1.05rem',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                border: 'none',
                boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
              }}>
                ➕ Report Scammer
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Search Bar with Enhanced Design */}
      <div style={{ marginBottom: '35px' }}>
        <div style={{ position: 'relative', maxWidth: '600px' }}>
          <span style={{
            position: 'absolute',
            left: '18px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '1.2rem',
            color: 'var(--text-secondary)'
          }}>
            🔍
          </span>
          <input
            type="text"
            placeholder="Search by scammer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '15px 20px 15px 50px',
              borderRadius: '12px',
              background: 'var(--card-bg)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--accent)';
              e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {/* Scammers Grid */}
      {scammers.length === 0 ? (
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '80px 20px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
          border: '2px dashed var(--border)',
          borderRadius: '16px'
        }}>
          <div style={{ fontSize: '5rem', marginBottom: '20px', opacity: 0.7 }}>📭</div>
          <h3 style={{ marginBottom: '15px', fontSize: '1.5rem' }}>No scammers found</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '25px', fontSize: '1.05rem' }}>
            {searchTerm ? 'Try adjusting your search terms' : 'The database is currently empty'}
          </p>
          {!searchTerm && (
            <Link to="/scammers/add" style={{ textDecoration: 'none' }}>
              <button className="btn-primary">Be the first to report</button>
            </Link>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: '24px',
          marginBottom: '40px'
        }}>
          {scammers.map((scammer) => (
            <div
              key={scammer._id}
              className="card"
              style={{
                padding: '0',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                border: '1px solid var(--border)',
                background: 'var(--card-bg)'
              }}
              onClick={() => setSelectedScammer(scammer)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
                e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              {/* Profile Picture with Overlay Gradient */}
              <div style={{
                width: '100%',
                height: '220px',
                background: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.4)), url(${scammer.profilePicture}) center/cover`,
                position: 'relative'
              }}>
                {/* Warning Badge */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '25px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  letterSpacing: '0.5px'
                }}>
                  <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                  SCAMMER
                </div>

                {/* Proof Count Badge */}
                <div style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  background: 'rgba(0, 0, 0, 0.75)',
                  backdropFilter: 'blur(8px)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>📸</span>
                  {scammer.proofScreenshots?.length || 0} proof{(scammer.proofScreenshots?.length || 0) !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Content Section */}
              <div style={{ padding: '24px' }}>
                <h3 style={{ 
                  marginBottom: '12px', 
                  fontSize: '1.35rem',
                  fontWeight: '700',
                  color: 'var(--text)',
                  lineHeight: '1.3'
                }}>
                  {scammer.name}
                </h3>
                
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.95rem',
                  marginBottom: '16px',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: '1.6',
                  minHeight: '72px'
                }}>
                  {scammer.reason}
                </p>

                {/* Info Tags */}
                <div style={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  {scammer.profileLinks && scammer.profileLinks.length > 0 && (
                    <span style={{
                      background: 'rgba(59, 130, 246, 0.15)',
                      color: '#60a5fa',
                      padding: '5px 12px',
                      borderRadius: '6px',
                      fontSize: '0.82rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      � {scammer.profileLinks.length} link{scammer.profileLinks.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {scammer.twitterId && (
                    <span style={{
                      background: 'rgba(29, 155, 240, 0.15)',
                      color: '#1d9bf0',
                      padding: '5px 12px',
                      borderRadius: '6px',
                      fontSize: '0.82rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      𝕏 Twitter
                    </span>
                  )}
                  {scammer.googleDriveLink && (
                    <span style={{
                      background: 'rgba(34, 197, 94, 0.15)',
                      color: '#4ade80',
                      padding: '5px 12px',
                      borderRadius: '6px',
                      fontSize: '0.82rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      📁 Drive
                    </span>
                  )}
                </div>

                {/* Footer */}
                <div style={{ 
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>👤</span>
                    <span style={{ color: 'var(--accent)', fontWeight: '600' }}>
                      {scammer.addedBy?.username}
                    </span>
                  </div>
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    color: 'var(--accent)',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    View Details →
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '15px', 
          marginTop: '40px',
          padding: '20px',
          background: 'var(--card-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary"
            style={{ 
              opacity: page === 1 ? 0.4 : 1,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            ← Previous
          </button>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 24px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.3)'
          }}>
            <span style={{ 
              fontWeight: '700',
              fontSize: '1.1rem',
              color: 'var(--accent)'
            }}>
              {page}
            </span>
            <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>of</span>
            <span style={{ fontWeight: '600', fontSize: '1.05rem' }}>{totalPages}</span>
          </div>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary"
            style={{ 
              opacity: page === totalPages ? 0.4 : 1,
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            Next →
          </button>
        </div>
      )}

      {/* Scammer Detail Modal */}
      {selectedScammer && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto',
            animation: 'fadeIn 0.2s ease'
          }}
          onClick={() => setSelectedScammer(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              margin: '20px',
              position: 'relative',
              animation: 'slideUp 0.3s ease',
              background: 'var(--card-bg)',
              border: '1px solid rgba(220, 38, 38, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedScammer(null)}
              style={{
                position: 'sticky',
                top: '20px',
                left: '100%',
                marginLeft: '-60px',
                background: 'rgba(220, 38, 38, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '45px',
                height: '45px',
                fontSize: '1.8rem',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(90deg) scale(1.1)';
                e.currentTarget.style.background = 'rgba(185, 28, 28, 0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(0) scale(1)';
                e.currentTarget.style.background = 'rgba(220, 38, 38, 0.9)';
              }}
            >
              ×
            </button>

            {/* Header Image */}
            <div style={{ position: 'relative', marginTop: '-45px' }}>
              <img
                src={selectedScammer.profilePicture}
                alt={selectedScammer.name}
                style={{ 
                  width: '100%', 
                  height: '350px', 
                  objectFit: 'cover',
                  borderRadius: '12px 12px 0 0'
                }}
              />
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))',
                borderRadius: '12px 12px 0 0'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '30px',
                right: '30px'
              }}>
                <h2 style={{ 
                  marginBottom: '10px',
                  fontSize: '2.2rem',
                  fontWeight: '800',
                  textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                  color: 'white'
                }}>
                  {selectedScammer.name}
                </h2>
                <div style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)'
                }}>
                  ⚠️ CONFIRMED SCAMMER
                </div>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '30px' }}>
              {/* Quick Info Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '30px'
              }}>
                {selectedScammer.twitterId && (
                  <div style={{
                    padding: '15px',
                    borderRadius: '10px',
                    background: 'rgba(29, 155, 240, 0.1)',
                    border: '1px solid rgba(29, 155, 240, 0.3)'
                  }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                      Twitter/X ID
                    </div>
                    <div style={{ fontWeight: '600', color: '#1d9bf0' }}>
                      @{selectedScammer.twitterId}
                    </div>
                  </div>
                )}
                <div style={{
                  padding: '15px',
                  borderRadius: '10px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)'
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                    Evidence Count
                  </div>
                  <div style={{ fontWeight: '600', color: 'var(--accent)' }}>
                    📸 {selectedScammer.proofScreenshots?.length || 0} Screenshots
                  </div>
                </div>
                <div style={{
                  padding: '15px',
                  borderRadius: '10px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)'
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                    Profile Links
                  </div>
                  <div style={{ fontWeight: '600', color: '#22c55e' }}>
                    🔗 {selectedScammer.profileLinks?.length || 0} Links
                  </div>
                </div>
              </div>

              {/* Profile Links Section */}
              {selectedScammer.profileLinks && selectedScammer.profileLinks.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ 
                    marginBottom: '15px',
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    🔗 Known Profile Links
                  </h3>
                  <div style={{ 
                    display: 'grid',
                    gap: '12px'
                  }}>
                    {selectedScammer.profileLinks.map((link, idx) => (
                      <div key={idx} style={{
                        padding: '15px',
                        borderRadius: '10px',
                        background: 'rgba(59, 130, 246, 0.05)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                      }}>
                        <div style={{ 
                          fontSize: '0.85rem',
                          color: 'var(--text-secondary)',
                          marginBottom: '6px',
                          fontWeight: '600'
                        }}>
                          {link.websiteName}
                        </div>
                        <a 
                          href={link.link} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ 
                            color: '#60a5fa',
                            wordBreak: 'break-all',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          {link.link}
                          <span style={{ fontSize: '0.85rem' }}>↗</span>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Known Alts */}
              {selectedScammer.alts && selectedScammer.alts.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ 
                    marginBottom: '15px',
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    👥 Known Alternate Accounts
                  </h3>
                  <div style={{ 
                    display: 'grid',
                    gap: '12px'
                  }}>
                    {selectedScammer.alts.map((alt, idx) => (
                      <div key={idx} style={{
                        padding: '15px',
                        borderRadius: '10px',
                        background: 'rgba(29, 155, 240, 0.05)',
                        border: '1px solid rgba(29, 155, 240, 0.2)',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(29, 155, 240, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(29, 155, 240, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(29, 155, 240, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(29, 155, 240, 0.2)';
                      }}>
                        <div>
                          <div style={{ 
                            fontSize: '1.05rem',
                            color: 'var(--text)',
                            marginBottom: '6px',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ color: '#1d9bf0' }}>𝕏</span>
                            {alt.twitterName}
                          </div>
                          <div style={{ 
                            fontSize: '0.85rem',
                            color: 'var(--text-secondary)',
                            fontFamily: 'monospace'
                          }}>
                            ID: {alt.twitterId}
                          </div>
                        </div>
                        <a 
                          href={`https://twitter.com/i/user/${alt.twitterId}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #1d9bf0 0%, #1da1f2 100%)',
                            color: 'white',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(29, 155, 240, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          View Profile
                          <span style={{ fontSize: '0.85rem' }}>↗</span>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Google Drive Link */}
              {selectedScammer.googleDriveLink && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ 
                    marginBottom: '15px',
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    📁 Additional Evidence
                  </h3>
                  <a 
                    href={selectedScammer.googleDriveLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '15px 24px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      color: '#22c55e',
                      textDecoration: 'none',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(34, 197, 94, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    📂 View Evidence Folder on Google Drive ↗
                  </a>
                </div>
              )}

              {/* Reason Section */}
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ 
                  marginBottom: '15px',
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  📋 Scam Details
                </h3>
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: 'rgba(220, 38, 38, 0.05)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                  lineHeight: '1.8',
                  fontSize: '1.05rem'
                }}>
                  {selectedScammer.reason}
                </div>
              </div>

              {/* Proof Screenshots */}
              {selectedScammer.proofScreenshots && selectedScammer.proofScreenshots.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ 
                    marginBottom: '15px',
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    📸 Proof Screenshots ({selectedScammer.proofScreenshots.length})
                  </h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', 
                    gap: '15px' 
                  }}>
                    {selectedScammer.proofScreenshots.map((screenshot, idx) => (
                      <div
                        key={idx}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '2px solid var(--border)',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => window.open(screenshot, '_blank')}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.borderColor = 'var(--accent)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.borderColor = 'var(--border)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <img
                          src={screenshot}
                          alt={`Proof ${idx + 1}`}
                          style={{ 
                            width: '100%', 
                            height: '180px', 
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          #{idx + 1}
                        </div>
                        <div style={{
                          position: 'absolute',
                          bottom: '0',
                          left: '0',
                          right: '0',
                          padding: '8px',
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          fontSize: '0.85rem',
                          textAlign: 'center',
                          opacity: '0',
                          transition: 'opacity 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                        >
                          Click to enlarge ↗
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer Meta Info */}
              <div style={{ 
                paddingTop: '20px',
                marginTop: '20px',
                borderTop: '2px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
                fontSize: '0.95rem',
                color: 'var(--text-secondary)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.2rem' }}>👤</span>
                  <span>Reported by</span>
                  <span style={{ 
                    color: 'var(--accent)', 
                    fontWeight: '700',
                    padding: '4px 12px',
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '6px'
                  }}>
                    {selectedScammer.addedBy?.username}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.2rem' }}>📅</span>
                  <span>{new Date(selectedScammer.createdAt).toLocaleDateString('en-US', { 
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scammers;
